<script setup>
// Estúdio Social — Calendário editorial (Task 12). Tela PRINCIPAL do módulo
// (rota default de /social). Grade mensal com os posts do mês como chips
// coloridos por status; abre o PostEditor (Task 11) pra criar/editar/gerar
// por IA. Segue a direção visual da Task 10: tokens n-* pra tudo, exceto o
// badge de plataforma (aqui não usado — o chip do calendário é sobre
// STATUS do post, não sobre rede social, de propósito: nunca confundir "que
// rede" com "que estado", ver task-10-report.md).
//
// Mapeamento de cor "verde"/"vermelho" do brief -> vocabulário n-* do
// módulo (só existem tokens tri-tom pra slate/blue/amber/teal/ruby/gray/
// iris — não existe um n-green/n-red utilizável): published usa teal
// (mesmo tom que "Connected" na Task 10 e "published" no badge do
// PostEditor) e failed/partial usam ruby (falha parcial é da família do
// failed — mesmo critério do badge do PostEditor na Task 11).
//
// FOLLOW-UP (fora do escopo desta task): arrastar um chip pra outro dia pra
// reagendar. V1 exige abrir o post e mudar a data no editor.
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import {
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  addDays,
  format,
  getDay,
  isSameDay,
  isSameMonth,
} from 'date-fns';
import { useAccount } from 'dashboard/composables/useAccount';
import { useDateFnsLocale } from 'dashboard/composables/useDateFnsLocale';
import socialAPI from 'dashboard/api/social';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import PostEditor from './PostEditor.vue';
import SocialNav from './SocialNav.vue';

const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const { dateFnsLocale } = useDateFnsLocale();

// -- Status → estilo do chip (tokens n-*, tri-tom) ---------------------------
const STATUS_CHIP_TONES = {
  draft: 'border-n-slate-6 bg-n-slate-3 text-n-slate-11',
  approved: 'border-n-blue-6 bg-n-blue-3 text-n-blue-11',
  scheduled: 'border-n-amber-6 bg-n-amber-3 text-n-amber-11',
  publishing: 'border-n-amber-6 bg-n-amber-3 text-n-amber-11 animate-pulse',
  published: 'border-n-teal-6 bg-n-teal-3 text-n-teal-11',
  failed: 'border-n-ruby-6 bg-n-ruby-3 text-n-ruby-11',
  partial: 'border-n-ruby-6 bg-n-ruby-3 text-n-ruby-11',
};

const WEEKDAY_KEYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

const MAX_VISIBLE_PER_DAY = 2;

const ONBOARDING_STEPS = [
  {
    titleKey: 'SOCIAL_STUDIO.CALENDAR.EMPTY_MONTH.STEP_1_TITLE',
    descKey: 'SOCIAL_STUDIO.CALENDAR.EMPTY_MONTH.STEP_1_DESC',
  },
  {
    titleKey: 'SOCIAL_STUDIO.CALENDAR.EMPTY_MONTH.STEP_2_TITLE',
    descKey: 'SOCIAL_STUDIO.CALENDAR.EMPTY_MONTH.STEP_2_DESC',
  },
  {
    titleKey: 'SOCIAL_STUDIO.CALENDAR.EMPTY_MONTH.STEP_3_TITLE',
    descKey: 'SOCIAL_STUDIO.CALENDAR.EMPTY_MONTH.STEP_3_DESC',
  },
];

// -- Estado do mês visível ----------------------------------------------------
const viewDate = ref(startOfMonth(new Date()));

const monthLabel = computed(() =>
  format(viewDate.value, 'MMMM yyyy', { locale: dateFnsLocale.value })
);

const weekStartsOn = computed(
  () => dateFnsLocale.value?.options?.weekStartsOn ?? 0
);

const weekdayHeaders = computed(() => {
  const firstDay = startOfWeek(viewDate.value, {
    weekStartsOn: weekStartsOn.value,
  });
  return Array.from({ length: 7 }, (_, index) => {
    const day = addDays(firstDay, index);
    return t(`SOCIAL_STUDIO.CALENDAR.WEEKDAYS.${WEEKDAY_KEYS[getDay(day)]}`);
  });
});

// Range da GRADE VISÍVEL inteira (primeira célula à última, com o padding
// de dias do mês anterior/seguinte) — não só o mês em si. É isso que vai
// pro backend em from/to (comportamento padrão de calendário: os cantos da
// grade também mostram post) e é a mesma referência usada pra montar as
// células abaixo.
const gridRange = computed(() => {
  const start = startOfWeek(startOfMonth(viewDate.value), {
    weekStartsOn: weekStartsOn.value,
  });
  const end = endOfWeek(endOfMonth(viewDate.value), {
    weekStartsOn: weekStartsOn.value,
  });
  return { start, end };
});

// -- Posts da grade visível -----------------------------------------------------
const isLoadingPosts = ref(true);
const isLoadingAccounts = ref(true);
const isLoading = computed(
  () => isLoadingPosts.value || isLoadingAccounts.value
);
const loadError = ref(null); // { status, message } | null
const posts = ref([]);
const connectedAccountsCount = ref(0);

const hasConnectedAccounts = computed(() => connectedAccountsCount.value > 0);

// Data de referência do post no calendário: agendado usa scheduled_at,
// rascunho sem agendamento aparece no dia da criação (SocialPost.for_calendar
// no backend já filtra assim — aqui só espelhamos o mesmo critério pra
// agrupar visualmente).
const postDayKey = post => {
  const raw = post.scheduled_at || post.created_at;
  return format(new Date(raw), 'yyyy-MM-dd');
};

const postsByDay = computed(() => {
  const map = {};
  posts.value.forEach(post => {
    const key = postDayKey(post);
    if (!map[key]) map[key] = [];
    map[key].push(post);
  });
  return map;
});

const extractErrorMessage = (error, fallback) =>
  error.response?.data?.error || error.response?.data?.errors?.[0] || fallback;

// Guarda de corrida: troca de mês rápida (ou o refetch do `saved` do editor
// disparando enquanto uma navegação de mês ainda está em voo) pode fazer
// duas chamadas resolverem fora de ordem — sem isso, a resposta mais LENTA
// vence e pinta o mês errado na tela mesmo sendo a mais velha.
let lastRequestId = 0;

const fetchPosts = async () => {
  lastRequestId += 1;
  const requestId = lastRequestId;
  isLoadingPosts.value = true;
  loadError.value = null;
  try {
    const { start, end } = gridRange.value;
    const { data } = await socialAPI.posts({
      from: start.toISOString(),
      to: end.toISOString(),
    });
    if (requestId !== lastRequestId) return; // resposta obsoleta, ignora
    posts.value = Array.isArray(data) ? data : [];
  } catch (error) {
    if (requestId !== lastRequestId) return;
    loadError.value = {
      status: error.response?.status,
      message: extractErrorMessage(
        error,
        t('SOCIAL_STUDIO.CALENDAR.LOADING_ERROR')
      ),
    };
  } finally {
    if (requestId === lastRequestId) isLoadingPosts.value = false;
  }
};

// Só usada pra decidir o estado vazio (primeira visita vs. mês vazio de um
// cliente que já usa o módulo) — falha aqui não deve bloquear o calendário,
// então some com o loading e assume "já tem rede" pra não empurrar o CTA de
// onboarding em cima de um usuário que já está usando o produto.
const fetchAccountsCount = async () => {
  isLoadingAccounts.value = true;
  try {
    const { data } = await socialAPI.accounts();
    connectedAccountsCount.value = (data.accounts || []).filter(
      a => a.status === 'connected'
    ).length;
  } catch (error) {
    connectedAccountsCount.value = 1; // fail-open: não mostrar onboarding por erro de fetch
  } finally {
    isLoadingAccounts.value = false;
  }
};

onMounted(() => {
  fetchPosts();
  fetchAccountsCount();
});

// -- Navegação de mês ----------------------------------------------------------
const goToPrevMonth = () => {
  viewDate.value = subMonths(viewDate.value, 1);
  fetchPosts();
};
const goToNextMonth = () => {
  viewDate.value = addMonths(viewDate.value, 1);
  fetchPosts();
};
const goToToday = () => {
  viewDate.value = startOfMonth(new Date());
  fetchPosts();
};

// -- Grade de dias ---------------------------------------------------------------
const gridDays = computed(() => {
  const { start, end } = gridRange.value;
  const today = new Date();

  return eachDayOfInterval({ start, end }).map(date => {
    const key = format(date, 'yyyy-MM-dd');
    const dayPosts = postsByDay.value[key] || [];
    return {
      key,
      date,
      dayNumber: date.getDate(),
      isCurrentMonth: isSameMonth(date, viewDate.value),
      isToday: isSameDay(date, today),
      posts: dayPosts,
      visiblePosts: dayPosts.slice(0, MAX_VISIBLE_PER_DAY),
      overflowCount: Math.max(0, dayPosts.length - MAX_VISIBLE_PER_DAY),
    };
  });
});

// Conta só os posts cujo dia local cai no MÊS VISÍVEL — `posts.value` agora
// inclui o padding da grade (dias de mês vizinho), então usar o array
// inteiro aqui enganaria o estado vazio (mês sem post nenhum mostrando a
// grade normal só porque o vizinho tem post no canto).
const hasPostsThisMonth = computed(() =>
  posts.value.some(post =>
    isSameMonth(new Date(post.scheduled_at || post.created_at), viewDate.value)
  )
);

// -- Chip helpers ------------------------------------------------------------
const chipTone = post =>
  STATUS_CHIP_TONES[post.status] || STATUS_CHIP_TONES.draft;
const chipTime = post =>
  post.scheduled_at ? format(new Date(post.scheduled_at), 'HH:mm') : '';
const chipExcerpt = post => {
  // Título tem prioridade no chip (identificação rápida no calendário).
  if (post.title) return post.title;
  const words = (post.content || '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  const excerpt = words.slice(0, 6).join(' ');
  return words.length > 6 ? `${excerpt}…` : excerpt;
};

// -- Editor (Task 11) ----------------------------------------------------------
const editorRef = ref(null);
const openNewPost = () => editorRef.value?.open();
// O modo IA já é a primeira seção do editor por padrão — não há prop
// específica de "abrir em destaque" no PostEditor (ver task-11), então
// abrir vazio já entrega o comportamento pedido.
const openGeneratePost = () => editorRef.value?.open();
const openPost = id => editorRef.value?.open(id);

const onEditorSaved = () => {
  // HEADS-UP do revisor (T11): o payload do `saved` após publish vem com
  // status ainda `approved` (o job muda depois) — nunca atualizar a lista
  // otimisticamente aqui, sempre refetch.
  fetchPosts();
};

// -- Popover "+N" do dia --------------------------------------------------------
const dayPopoverRef = ref(null);
const dayPopoverDate = ref(null);
const dayPopoverPosts = ref([]);
const dayPopoverLabel = computed(() =>
  dayPopoverDate.value
    ? format(dayPopoverDate.value, 'PPP', { locale: dateFnsLocale.value })
    : ''
);
const openDayPopover = cell => {
  dayPopoverDate.value = cell.date;
  dayPopoverPosts.value = cell.posts;
  dayPopoverRef.value?.open();
};
const openPostFromPopover = id => {
  dayPopoverRef.value?.close();
  openPost(id);
};

// -- Erro de carregamento --------------------------------------------------------
const goToBilling = () =>
  router.push(accountScopedRoute('billing_settings_index'));
</script>

<template>
  <div class="w-full h-full overflow-y-auto bg-n-background">
    <div class="max-w-7xl mx-auto p-6 space-y-6">
      <SocialNav />

      <!-- Cabeçalho: texto estático (não depende de dado carregado, pra não
         piscar como o título da Task 10 — ver nota no topo do arquivo). -->
      <div
        class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between"
      >
        <div>
          <h1 class="text-xl font-semibold text-n-slate-12">
            {{ t('SOCIAL_STUDIO.CALENDAR.TITLE') }}
          </h1>
          <p class="mt-1 text-sm text-n-slate-11 max-w-md">
            {{ t('SOCIAL_STUDIO.CALENDAR.SUBTITLE') }}
          </p>
        </div>
        <div class="flex shrink-0 items-center gap-2">
          <Button
            variant="outline"
            icon="i-lucide-sparkles"
            :label="t('SOCIAL_STUDIO.CALENDAR.GENERATE_AI')"
            @click="openGeneratePost"
          />
          <Button
            icon="i-lucide-plus"
            :label="t('SOCIAL_STUDIO.CALENDAR.NEW_POST')"
            @click="openNewPost"
          />
        </div>
      </div>

      <!-- Navegação de mês -->
      <div
        class="flex items-center justify-between gap-3 rounded-2xl border border-n-weak bg-n-solid-2 p-3"
      >
        <div class="flex items-center gap-1">
          <button
            type="button"
            class="flex size-8 items-center justify-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
            :aria-label="t('SOCIAL_STUDIO.CALENDAR.PREV_MONTH')"
            @click="goToPrevMonth"
          >
            <span class="i-lucide-chevron-left size-4" />
          </button>
          <button
            type="button"
            class="rounded-lg px-2.5 py-1 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
            @click="goToToday"
          >
            {{ t('SOCIAL_STUDIO.CALENDAR.TODAY') }}
          </button>
          <button
            type="button"
            class="flex size-8 items-center justify-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
            :aria-label="t('SOCIAL_STUDIO.CALENDAR.NEXT_MONTH')"
            @click="goToNextMonth"
          >
            <span class="i-lucide-chevron-right size-4" />
          </button>
        </div>
        <h2 class="text-base font-semibold capitalize text-n-slate-12">
          {{ monthLabel }}
        </h2>
        <div class="w-[124px]" aria-hidden="true" />
      </div>

      <!-- Erro ao carregar (inclui 402: módulo não ativo) -->
      <div
        v-if="!isLoading && loadError"
        class="rounded-2xl border border-n-weak bg-n-solid-2 p-6 text-center space-y-3"
      >
        <span
          class="i-lucide-cloud-alert mx-auto block size-8 text-n-slate-10"
        />
        <p class="text-sm text-n-slate-11">{{ loadError.message }}</p>
        <Button
          v-if="loadError.status === 402"
          :label="t('SOCIAL_STUDIO.CALENDAR.UPGRADE_CTA')"
          size="sm"
          @click="goToBilling"
        />
        <Button
          v-else
          :label="t('SOCIAL_STUDIO.CALENDAR.RETRY')"
          variant="outline"
          size="sm"
          @click="fetchPosts"
        />
      </div>

      <!-- Loading: esqueleto no formato da grade (mesmo padrão da Task 10). -->
      <div
        v-else-if="isLoading"
        class="grid grid-cols-7 gap-1.5 rounded-2xl border border-n-weak bg-n-solid-2 p-3"
      >
        <div
          v-for="n in 35"
          :key="n"
          class="h-[104px] animate-pulse rounded-xl bg-n-slate-3"
        />
      </div>

      <template v-else>
        <!-- Estado vazio: mês sem nenhum post. Sempre explica o fluxo em 3
           passos; o CTA principal muda conforme já haver rede conectada
           (primeira visita de verdade) ou não (mês vazio de um cliente que
           já usa o módulo). -->
        <div
          v-if="!hasPostsThisMonth"
          class="rounded-2xl border border-n-weak bg-n-solid-2 p-6 sm:p-8"
        >
          <div class="mx-auto max-w-lg text-center">
            <h3 class="text-lg font-semibold text-n-slate-12">
              {{
                hasConnectedAccounts
                  ? t('SOCIAL_STUDIO.CALENDAR.EMPTY_MONTH.TITLE_WITH_ACCOUNTS')
                  : t('SOCIAL_STUDIO.CALENDAR.EMPTY_MONTH.TITLE_NO_ACCOUNTS')
              }}
            </h3>
            <p class="mt-1 text-sm text-n-slate-11">
              {{
                hasConnectedAccounts
                  ? t(
                      'SOCIAL_STUDIO.CALENDAR.EMPTY_MONTH.SUBTITLE_WITH_ACCOUNTS'
                    )
                  : t('SOCIAL_STUDIO.CALENDAR.EMPTY_MONTH.SUBTITLE_NO_ACCOUNTS')
              }}
            </p>
          </div>

          <div class="mx-auto mt-6 grid max-w-3xl gap-3 sm:grid-cols-3">
            <div
              v-for="(step, index) in ONBOARDING_STEPS"
              :key="step.titleKey"
              class="flex flex-col gap-1.5 rounded-xl border border-n-weak bg-n-solid-1 p-4"
            >
              <span
                class="flex size-7 items-center justify-center rounded-lg border border-n-blue-6 bg-n-blue-3 text-xs font-semibold text-n-blue-11"
              >
                {{ index + 1 }}
              </span>
              <p class="text-sm font-medium text-n-slate-12">
                {{ t(step.titleKey) }}
              </p>
              <p class="text-xs text-n-slate-10">
                {{ t(step.descKey) }}
              </p>
            </div>
          </div>

          <div class="mt-6 flex flex-wrap items-center justify-center gap-2">
            <RouterLink
              v-if="!hasConnectedAccounts"
              :to="accountScopedRoute('settings_inbox_new')"
            >
              <Button
                :label="t('SOCIAL_STUDIO.CALENDAR.EMPTY_MONTH.CTA_CONNECT')"
                icon="i-lucide-share-2"
              />
            </RouterLink>
            <Button
              v-else
              icon="i-lucide-sparkles"
              :label="t('SOCIAL_STUDIO.CALENDAR.EMPTY_MONTH.CTA_GENERATE')"
              @click="openGeneratePost"
            />
            <Button
              variant="outline"
              icon="i-lucide-plus"
              :label="t('SOCIAL_STUDIO.CALENDAR.EMPTY_MONTH.CTA_NEW_POST')"
              @click="openNewPost"
            />
          </div>
        </div>

        <!-- Grade mensal -->
        <div v-else class="overflow-hidden rounded-2xl border border-n-weak">
          <div
            class="grid grid-cols-7 divide-x divide-n-weak border-b border-n-weak bg-n-solid-2"
          >
            <div
              v-for="weekday in weekdayHeaders"
              :key="weekday"
              class="p-2 text-center text-xs font-semibold uppercase tracking-wide text-n-slate-10"
            >
              {{ weekday }}
            </div>
          </div>
          <div class="grid grid-cols-7 divide-x divide-y divide-n-weak">
            <div
              v-for="cell in gridDays"
              :key="cell.key"
              class="flex min-h-[110px] flex-col gap-1 p-1.5"
              :class="[
                cell.isCurrentMonth
                  ? 'bg-n-solid-1'
                  : 'bg-n-solid-2 opacity-60',
              ]"
            >
              <span
                class="flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-medium"
                :class="[
                  cell.isToday ? 'bg-n-brand text-white' : 'text-n-slate-11',
                  !cell.isCurrentMonth && !cell.isToday && 'text-n-slate-9',
                ]"
              >
                {{ cell.dayNumber }}
              </span>

              <div class="flex flex-col gap-1">
                <button
                  v-for="post in cell.visiblePosts"
                  :key="post.id"
                  type="button"
                  class="flex w-full min-w-0 items-center gap-1 rounded-md border px-1.5 py-0.5 text-left text-[11px] leading-tight"
                  :class="[
                    chipTone(post),
                    !post.scheduled_at && 'border-dashed',
                  ]"
                  @click="openPost(post.id)"
                >
                  <span
                    v-if="chipTime(post)"
                    class="shrink-0 font-semibold tabular-nums"
                  >
                    {{ chipTime(post) }}
                  </span>
                  <span class="truncate">{{ chipExcerpt(post) }}</span>
                </button>

                <button
                  v-if="cell.overflowCount > 0"
                  type="button"
                  class="w-full rounded-md px-1.5 py-0.5 text-left text-[11px] font-medium text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-11"
                  @click="openDayPopover(cell)"
                >
                  {{
                    t('SOCIAL_STUDIO.CALENDAR.MORE', {
                      count: cell.overflowCount,
                    })
                  }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Popover simples do dia (3+ posts) -->
      <Dialog
        ref="dayPopoverRef"
        type="alert"
        :title="dayPopoverLabel"
        :show-cancel-button="false"
        :show-confirm-button="false"
        width="sm"
      >
        <div class="flex flex-col gap-2">
          <button
            v-for="post in dayPopoverPosts"
            :key="post.id"
            type="button"
            class="flex w-full min-w-0 items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left text-sm"
            :class="[chipTone(post), !post.scheduled_at && 'border-dashed']"
            @click="openPostFromPopover(post.id)"
          >
            <span
              v-if="chipTime(post)"
              class="shrink-0 font-semibold tabular-nums"
            >
              {{ chipTime(post) }}
            </span>
            <span class="truncate">{{
              chipExcerpt(post) || post.content
            }}</span>
          </button>
        </div>
      </Dialog>

      <!-- Editor de post (Task 11), instanciado dentro desta página. -->
      <PostEditor ref="editorRef" @saved="onEditorSaved" />
    </div>
  </div>
</template>
